// V2 hardware_health_status_dashboard. Master-admin view of controller
// hardware health.

import { useCallback } from 'react';
import { Icon } from '@ng-unifi/ui';
import { useNavigate } from 'react-router-dom';
import { ResourceList } from '../components/ResourceList.js';
import { apiClient } from '../lib/api.js';
import { useFocusParam } from '../lib/useFocusParam.js';
import { useAuth } from '../auth/AuthContext.js';
import { statusLabel } from '../lib/humanLabels.js';

interface DeviceRow {
  id: string;
  unifi_device_id: string;
  name: string;
  kind: string;
  status: 'online' | 'upgrading' | 'offline';
  last_heartbeat: string | null;
  label_override: string | null;
}

interface MasterInventoryDoor {
  id: string;
  name: string;
  full_name?: string | null;
  status?: 'online' | 'offline' | 'unknown';
  online?: boolean;
}

interface MasterInventoryDevice {
  id: string;
  unifi_device_id?: string;
  name: string;
  kind?: string;
  status?: DeviceRow['status'];
  last_heartbeat?: string | null;
}

interface MasterInventoryResponse {
  data: {
    doors: MasterInventoryDoor[];
    devices: MasterInventoryDevice[];
  };
}

function deviceStatus(status: MasterInventoryDevice['status']): DeviceRow['status'] {
  if (status === 'offline' || status === 'upgrading') return status;
  return 'online';
}

function doorStatus(door: MasterInventoryDoor): DeviceRow['status'] {
  if (door.status === 'online' || door.online === true) return 'online';
  return 'offline';
}

async function loadMasterInventory(): Promise<DeviceRow[]> {
  const res = await apiClient.get<MasterInventoryResponse>(
    '/api/v1/admin/hardware/inventory',
    { skipTenantScope: true },
  );
  return [
    ...res.data.doors.map((door) => ({
      id: `door:${door.id}`,
      unifi_device_id: door.id,
      name: door.full_name ?? door.name,
      kind: 'door',
      status: doorStatus(door),
      last_heartbeat: null,
      label_override: null,
    })),
    ...res.data.devices.map((device) => ({
      id: `device:${device.id}`,
      unifi_device_id: device.unifi_device_id ?? device.id,
      name: device.name,
      kind: device.kind ?? 'device',
      status: deviceStatus(device.status),
      last_heartbeat: device.last_heartbeat ?? null,
      label_override: null,
    })),
  ];
}

function compactHardwareName(value: string): string {
  const parts = value
    .split(/\s+-\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) return parts.slice(2).join(' - ');
  return value;
}

const TONE: Record<DeviceRow['status'], string> = {
  online: 'bg-secondary-container text-on-secondary-container',
  upgrading: 'bg-tertiary-fixed text-on-tertiary-fixed',
  offline: 'bg-error-container text-on-error-container',
};

export function HardwareHealthPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';
  const load = useCallback(() => loadMasterInventory(), []);
  // UX-014: DoorDetailPage's "Rename" sends the operator back here with
  // ?focus=<unifi_door_id>. Nothing read it, so they landed at the top of an
  // unsorted list and had to find the door again by eye.
  const { focusProps } = useFocusParam();
  // GAP-N2 (audit 2026-05-21): door permissions are a master-only first-run
  // and occasional administration task, so surface it from Hardware.
  if (!isMaster) {
    return (
      <div className="rounded-xl border border-outline-variant bg-surface-container-lowest p-6">
        <h1 className="font-headline-sm text-headline-sm text-on-surface">Hardware health</h1>
        <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
          Hardware health is available to site administrators.
        </p>
      </div>
    );
  }
  return (
    <ResourceList<DeviceRow>
      title="Hardware health"
      subtitle="Building-owned controller resources across this site."
      icon="dns"
      load={load}
      emptyMessage="No controller resources found. Refresh building resources after adding hardware in UniFi Access."
      // UX-030: nothing in the product linked to /hardware/doors/:id, so every
      // control on the door detail page — unlock, per-door lockdown, rename —
      // was reachable only by typing the URL. Doors only; controller devices
      // have no detail page.
      rowHref={(d) =>
        d.id.startsWith('door:') ? `/hardware/doors/${encodeURIComponent(d.unifi_device_id)}` : null
      }
      rowProps={(d) => focusProps(d.unifi_device_id)}
      action={{
        label: 'Door permissions',
        icon: 'add_circle',
        onClick: () => navigate('/hardware/claim'),
      }}
      columns={[
        {
          key: 'name',
          header: 'Device',
          render: (d) => (
            <div className="flex items-center gap-2">
              <Icon name="door_front" className="text-on-surface-variant" />
              <span>{compactHardwareName(d.label_override ?? d.name)}</span>
            </div>
          ),
        },
        { key: 'kind', header: 'Kind', width: '14%' },
        {
          key: 'status',
          header: 'Status',
          width: '14%',
          render: (d) => (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${TONE[d.status]}`}
            >
              {statusLabel(d.status)}
            </span>
          ),
        },
        {
          key: 'source',
          header: 'Source',
          width: '18%',
          render: (d) => (d.id.startsWith('door:') ? 'Door inventory' : 'Controller device'),
        },
      ]}
    />
  );
}
